import React, { useContext, useEffect, useState, useCallback } from 'react';
import { ShopContext } from 'Context/ShopContext';
import { Paper, Button, Tooltip } from '@mui/material';
import { USER_PERMISSIONS } from 'const';
import { AlertMessagesContext } from 'react-alert-messages';
import CustomerService from 'services/CustomerService';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import NewCustomerGroupDialog from 'components/popups/NewCustomerGroupDialog';
import EditCustomerGroupDialog from 'components/popups/EditCustomerGroupDialog';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { SUBSCRIPTION_TYPE } from 'const';
import { InventoryContext } from 'Context/InventoryContext';
import { UserContext } from 'Context/UserContext';
import styles from './styles.module.css';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import Info from 'components/utils/Alert/Info';
import DataTable from 'components/utils/DataTable';

function CustomerGroup() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { getPriceCategories } = useContext(InventoryContext);
  const subscriptionType = shop?.subscription?.type;

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [addTab, setAddTab] = useState('');
  const [groups, setGroups] = useState();
  const [editData, setEditData] = useState('');



  const toggleAddTab = (move = '') => {
    if (move === 'save') getCustomerGroups();
    addTab ? setAddTab('') : setAddTab('open');
  };

  const toggleEditTab = (rowData = '') => {
    editData ? setEditData('') : setEditData(rowData);
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Edit'),
      id: 'updateItems',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Tooltip title="Edit" onClick={() => toggleEditTab(item)}>
            <EditIcon className={styles.iconButton} />
          </Tooltip>
        );
      },
    },
  ];

  if (subscriptionType === SUBSCRIPTION_TYPE.PREMIUM) {
    headerData.splice(2, 0, {
      label: tCommon('Price Category'),
      id: 'price_category',
      type: 'callback',
      viewRender: (item) => (item?.price_category ? item?.price_category?.name : '-'),
    });
  }

  const getCustomerGroups = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const res = await CustomerService.getCustomerGroups();
      setGroups(res);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  }, [postAlertMessage]);

  const userHasCustomersWritePermission = user?.shop_permissions?.customers_permission === USER_PERMISSIONS.WRITE;
  const featureValidity = shop?.customer_support_validity;

  useEffect(() => {
    if (!shop?.id || featureIsExpired(featureValidity)) return;
    getCustomerGroups();
  }, [featureValidity, getCustomerGroups, shop?.id]);

  useEffect(() => {
    if (subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.PREMIUM) {
      getPriceCategories();
    }
  }, [getPriceCategories, subscriptionType]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Customer Group')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
      </div>
      {shop && featureIsExpired(featureValidity) ? (
        <RestrictionInfo
          title={tOperations('Customer Support Feature Not Available')}
          content={tOperations('To get customer support options, contact support team.')}
        />
      ) : (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec} style={{ justifyContent: 'flex-end' }}>
              <div className={styles.filerInputSec}>
                {userHasCustomersWritePermission && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: '#ff851b' }}
                    className={styles.actionBtn}
                    onClick={toggleAddTab}
                  >
                    <NoteAddIcon className={styles.actionBtnIcon} />
                    {tCommon('New Group')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Paper className={styles.tableWrapper}>
        <DataTable columns={headerData} rows={groups || []} isLoading={loadingIndicator} deactiveRow={true} />
      </Paper>
      {!groups && !loadingIndicator && (
        <Info
          title={tOperations('No Customers Found')}
          content={tOperations('You have no customers to list. Create a new customer to get started.')}
        />
      )}
      {editData && (
        <EditCustomerGroupDialog
          toggleEditTab={toggleEditTab}
          editData={editData}
          getCustomerGroups={getCustomerGroups}
        />
      )}
      {addTab && <NewCustomerGroupDialog toggleAddTab={toggleAddTab} />}
    </div>
  );
}

export default withConsoleBase(CustomerGroup);
